import { SourceType } from "../SourcesField/SelectSource";

/**
 * Flattens a Record<SourceType, string[]> into a simple string[] array
 * @param sources - The sources object
 * @returns A flattened array of source strings
 */
export const flattenSources = (sources: Record<SourceType, string[]> | undefined): string[] => {
  if (!sources) {
    return [];
  }

  const flattened: string[] = [];
  Object.values(sources).forEach(sourceList => {
    flattened.push(...sourceList);
  });

  return flattened;
};

/**
 * Gets sources of a specific type from the sources object
 * @param sources - The sources object
 * @param sourceType - The type of sources to retrieve
 * @returns Array of sources of the specified type
 */
export const getSourcesByType = (
  sources: Record<SourceType, string[]> | undefined,
  sourceType: SourceType,
): string[] => {
  if (!sources) {
    return [];
  }

  return sources[sourceType] || [];
};

/**
 * Checks if a source exists in the sources object
 * @param sources - The sources object
 * @param sourceValue - The source value to check for
 * @returns Boolean indicating if the source exists
 */
export const hasSource = (
  sources: Record<SourceType, string[]> | undefined,
  sourceValue: string,
): boolean => {
  if (!sources) {
    return false;
  }

  return Object.values(sources).some(sourceList => sourceList.includes(sourceValue));
};

/**
 * Gets the type of a specific source
 * @param sources - The sources object
 * @param sourceValue - The source value to get the type for
 * @param defaultType - The default type to return if not found
 * @returns The SourceType of the given source
 */
export const getSourceType = (
  sources: Record<SourceType, string[]> | undefined,
  sourceValue: string,
  defaultType: SourceType = SourceType.Column,
): SourceType => {
  if (!sources) {
    return defaultType;
  }

  for (const [sourceType, sourceList] of Object.entries(sources)) {
    if (sourceList.includes(sourceValue)) {
      return sourceType as SourceType;
    }
  }

  return defaultType;
};

/**
 * Creates an empty sources object with all source types initialized to empty arrays
 * @returns An empty sources object
 */
export const createEmptySources = (): Record<SourceType, string[]> => ({
  [SourceType.Column]: [],
  [SourceType.Dataset]: [],
  [SourceType.InputVariable]: [],
  [SourceType.Dependency]: [],
});

/**
 * Filters sources excluding those that are already selected
 * @param sources - The sources object
 * @param selectedSources - Array of already selected source values
 * @returns Filtered sources object with selected sources removed
 */
export const filterAvailableSources = (
  sources: Record<SourceType, string[]> | undefined,
  selectedSources: string[],
): Record<SourceType, string[]> => {
  if (!sources) {
    return createEmptySources();
  }

  const filtered: Record<SourceType, string[]> = {} as Record<SourceType, string[]>;

  Object.entries(sources).forEach(([sourceType, sourceList]) => {
    const filteredList = sourceList.filter(source => !selectedSources.includes(source));
    filtered[sourceType as SourceType] = filteredList;
  });

  return filtered;
};
